import { UseFormReturn } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Plus, X } from 'lucide-react'
import { type ForensicsEnvironmentFormData } from './schema'
import { useVaults } from '@/features/vaults/hooks'
import { useComputeObjects, useForensicsObjectVersions } from './hooks'
import type { ForensicsExaminableObject } from './api'
import { formatDateTime } from '@/lib/dateUtils'

interface ForensicsFormProps {
    form: UseFormReturn<ForensicsEnvironmentFormData>
    isEditing?: boolean
}

export function ForensicsForm({ form, isEditing }: ForensicsFormProps) {
    const { data: vaultsResponse } = useVaults()
    const vaults = (vaultsResponse as any)?.data || vaultsResponse || []
    const { data: computeObjects } = useComputeObjects()
    const computeObjectsList = Array.isArray(computeObjects) ? computeObjects : []

    const servers = form.watch('servers') || []

    const addServer = () => {
        const current = form.getValues('servers') || []
        form.setValue('servers', [...current, { computeObjectId: '', computeObjectVersionId: undefined }])
    }

    const removeServer = (index: number) => {
        const current = form.getValues('servers') || []
        form.setValue('servers', current.filter((_, i) => i !== index))
    }

    return (
        <div className="space-y-4">
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Environment Name</FormLabel>
                        <FormControl>
                            <Input placeholder="Enter environment name" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="vaultId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Vault</FormLabel>
                        <Select
                            onValueChange={(val) => field.onChange(parseInt(val, 10))}
                            value={field.value ? String(field.value) : undefined}
                            disabled={isEditing}
                        >
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select vault" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {(Array.isArray(vaults) ? vaults : []).map((v: any) => (
                                    <SelectItem key={v.id} value={String(v.id)}>
                                        {v.vaultname || v.name || v.VaultID || `Vault ${v.id}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="securityGroupId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Security Group ID</FormLabel>
                        <FormControl>
                            <Input placeholder="sg-xxxxxxxxx" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="awsAccountId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>AWS Account ID</FormLabel>
                        <FormControl>
                            <Input placeholder="123456789012" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Optional description" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            {/* Servers section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <FormLabel>Servers</FormLabel>
                    <Button type="button" variant="outline" size="sm" onClick={addServer}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Server
                    </Button>
                </div>
                {servers.map((_server, index) => (
                    <ServerRow
                        key={index}
                        index={index}
                        form={form}
                        computeObjects={computeObjectsList}
                        onRemove={() => removeServer(index)}
                    />
                ))}
                {servers.length === 0 && (
                    <p className="text-sm text-muted-foreground">No servers added. Click "Add Server" to add compute objects to this environment.</p>
                )}
            </div>
        </div>
    )
}

function ServerRow({
    index,
    form,
    computeObjects,
    onRemove,
}: {
    index: number
    form: UseFormReturn<ForensicsEnvironmentFormData>
    computeObjects: ForensicsExaminableObject[]
    onRemove: () => void
}) {
    const selectedObjectId = form.watch(`servers.${index}.computeObjectId`)
    // Only the versions that carry a disk image. The previous code listed EVERY version of the
    // object, so an investigator could pick one with nothing to mount — AD1 has 116 mountable
    // versions out of 139 — and the workstation would provision and then fail to attach.
    const { data: versionsForObject = [], isLoading: isLoadingVersions } =
        useForensicsObjectVersions(selectedObjectId)
    const selectedObject = computeObjects.find((o) => o.ObjectID === selectedObjectId)

    return (
        <div className="flex items-end gap-2 p-3 border rounded-lg">
            <div className="flex-1">
                <FormField
                    control={form.control}
                    name={`servers.${index}.computeObjectId`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">Object ID</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || undefined}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select compute object" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {computeObjects.map((obj) => (
                                        <SelectItem key={obj.ObjectID} value={obj.ObjectID}>
                                            <span className="font-medium">{obj.ObjectID}</span>
                                            {obj.Description ? ` — ${obj.Description}` : ''}
                                            {obj.Classification ? (
                                                <span className="text-muted-foreground">
                                                    {' · '}{obj.Classification}
                                                </span>
                                            ) : null}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <div className="flex-1">
                <FormField
                    control={form.control}
                    name={`servers.${index}.computeObjectVersionId`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">Version</FormLabel>
                            <Select
                                onValueChange={(val) => field.onChange(parseInt(val, 10))}
                                value={field.value ? String(field.value) : undefined}
                                disabled={!selectedObjectId}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select version" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {versionsForObject.map((v) => (
                                        <SelectItem key={v.versionID} value={String(v.versionID)}>
                                            {v.objectSecondary} —{' '}
                                            {v.vaultDatetime
                                                ? formatDateTime(v.vaultDatetime)
                                                : 'No date'}
                                            <span className="text-muted-foreground">
                                                {' · '}{v.sourceFormat}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {/* Say why some of the object's history is missing from the list.
                                Without this, an investigator who knows AD1 has 139 copies sees 116
                                and has no way to tell whether the rest are hidden, lost, or never
                                existed. */}
                            {selectedObject && !isLoadingVersions
                                && selectedObject.MountableVersions < selectedObject.TotalVersions && (
                                <p className="text-[11px] text-muted-foreground">
                                    {selectedObject.MountableVersions} of {selectedObject.TotalVersions}{' '}
                                    copies hold a disk image that can be opened.
                                </p>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="shrink-0">
                <X className="h-4 w-4" />
            </Button>
        </div>
    )
}
